import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BDMatrixEliminationCategory } from '@models/bdMatrixEliminationCategory.model';
import { NgModel } from '@angular/forms';
import { RegularExpressionPattern, ConfirmationResult, ConfirmationMessage } from '@shared/_constants';
import { List } from 'linqts-es-module';
import { DialogService } from '@services/dialog.service';
import { BdMatrixService } from '@services/bd-matrix.service';
import { BDMatrixFilter } from '@models/bdMatrixFilter.model';
import { EliminationMatrixFilter } from '@models/eliminationMatrixFilterModel';

@Component({
  selector: 'app-elimination-criteria-dialog',
  templateUrl: './elimination-criteria-dialog.component.html',
})
export class EliminationCriteriaDialogComponent implements OnInit {
  searchStrategyId: number;
  regularExpressionPattern = RegularExpressionPattern;
  submitted = false;
  duplicateCategory = false;
  blockThisUI = false;
  blockButton = false;
  showInMasterMatrix: boolean;

  eliminationCategories: BDMatrixEliminationCategory[] = [];
  eliminationCategory: BDMatrixEliminationCategory = new BDMatrixEliminationCategory();
  lstEliminationCategories: List<BDMatrixEliminationCategory> = new List<BDMatrixEliminationCategory>();

  results: any[] = [];
  bdMatrixFilter: BDMatrixFilter = new BDMatrixFilter();
  filterCriteria: EliminationMatrixFilter = new EliminationMatrixFilter();
  eliminationCriteriaColumns: BDMatrixEliminationCategory[] = [];
  minCategory = 1;
  maxCategory = 22;
  displayOrder = 0;

  constructor(private activeModal: NgbActiveModal, private dialogService: DialogService, private bdMatrixService: BdMatrixService) {}

  get displayEliminationCategories(): List<BDMatrixEliminationCategory> {
    return this.lstEliminationCategories.Where((a) => a.isActive === true).ToList();
  }

  async ngOnInit() {
    try {
      this.blockThisUI = true;
      const eliminationCriteriaData = await this.bdMatrixService.getEliminationCriteriaCategory(this.searchStrategyId, this.showInMasterMatrix);

      this.eliminationCategories = eliminationCriteriaData;

      if (this.eliminationCategories && this.eliminationCategories.length > 0) {
        this.lstEliminationCategories.AddRange(this.eliminationCategories);
        this.displayOrder = this.lstEliminationCategories.Where((a) => a.isActive === true).Max((a) => a.displayOrder);
      }

      this.eliminationCriteriaColumns = eliminationCriteriaData;

      await this.getEliminationMatrixData();
    } finally {
      this.blockThisUI = false;
    }
  }

  async close() {
    if (this.eliminationCategories.length !== this.lstEliminationCategories.Where((a) => a.isActive === true).Count()) {
      const confirmationResult = await this.dialogService.confirm('Please Confirm', ConfirmationMessage.UnsavedModifications, 'Yes', 'No');
      if (confirmationResult === ConfirmationResult.yes) {
        this.activeModal.close();
      }
    } else {
      this.activeModal.close();
    }
  }

  async save() {
    this.blockButton = true;

    this.eliminationCategories = this.lstEliminationCategories.ToArray();

    await this.bdMatrixService.SaveEliminationCriteriaColumn(this.eliminationCategories);

    const getUpdated = await this.bdMatrixService.getEliminationCriteriaCategory(this.searchStrategyId, this.showInMasterMatrix);

    this.activeModal.close(getUpdated);

    this.blockButton = false;
  }

  addCategory() {
    this.submitted = true;
    this.duplicateCategory = false;

    // Check duplicate name
    if (
      this.lstEliminationCategories
        .Where((a) => a.isActive)
        .Count((a) => a.displayName.toLowerCase() === this.eliminationCategory.displayName.toLowerCase()) > 0
    ) {
      this.duplicateCategory = true;
    } else {
      this.eliminationCategory.bdMatrixEliminationCategoryId = 0;
      this.eliminationCategory.isActive = true;
      this.eliminationCategory.displayOrder = this.displayOrder + 1;
      this.eliminationCategory.studySearchStrategyId = this.searchStrategyId;
      this.eliminationCategory.showInMasterMatrix = this.showInMasterMatrix;
      this.lstEliminationCategories.Add(this.eliminationCategory);

      // Reset value
      this.submitted = false;
      this.eliminationCategory = new BDMatrixEliminationCategory();
      this.displayOrder = this.lstEliminationCategories.Max((a) => a.displayOrder);
    }
  }

  async removeCategory(category) {
    const bdMatrixEliminationCategory = category as BDMatrixEliminationCategory;
    if (bdMatrixEliminationCategory.displayOrder !== 4 && bdMatrixEliminationCategory.displayOrder !== 6) {
      let categoryColumnSelected = false;
      const confirmationResult = await this.dialogService.confirm('Please Confirm', ConfirmationMessage.EliminationCategory, 'Yes', 'No');
      if (confirmationResult === ConfirmationResult.yes) {
        const result = this.lstEliminationCategories.Where((a) => a === category).FirstOrDefault();
        if (result) {
          // Check in the BDMatrixResults if the elimination category column is selected for any comparable
          this.results.forEach(function (item) {
            if (item[result.bdMatrixEliminationCategoryId] === 1) {
              categoryColumnSelected = true;
            }
          });
          if (categoryColumnSelected) {
            const secondConfirmationResult = await this.dialogService.confirm('Please Confirm', ConfirmationMessage.EliminationCriteria, 'Yes', 'No');
            if (secondConfirmationResult === ConfirmationResult.yes) {
              result.isActive = false;
            }
          } else {
            result.isActive = false;
          }
        }
        this.displayOrder = this.lstEliminationCategories.Where((a) => a.isActive).Max((a) => a.displayOrder);
      }
    }
  }

  ShowValidationMessage(item: NgModel): boolean {
    if (this.submitted) {
      return item.errors && (this.submitted || !item.pristine || item.touched);
    } else {
      return false;
    }
  }

  private async getEliminationMatrixData() {
    this.bdMatrixFilter.studySearchStrategyId = this.searchStrategyId;
    this.bdMatrixFilter.filterParameter = this.filterCriteria.filters;
    this.bdMatrixFilter.searchText = this.filterCriteria.searchText;
    const result = await this.bdMatrixService.GetBDMatrix(this.bdMatrixFilter);

    if (result) {
      this.results = result && result.allResults.length > 0 ? JSON.parse(result.allResults[0].toString()) : [];
    } else {
      this.results = [];
    }
  }

  isAllowDeleteOnCriteria(displayOrder: number): boolean {
    if (!(displayOrder === 4 || displayOrder === 6 || displayOrder === 1 || displayOrder === 2)) {
      return true;
    }
    return false;
  }
}
